import { ContactModalForm } from 'modules/CustomerContacts'
import { useNotifications, SimpleModal } from '@sequencehq/core-components'
import {
  mapApiResponseToFieldValidationErrors,
  useForm,
  ValidationError
} from '@sequencehq/utils'
import { isEmail, required } from '@sequencehq/utils/validation'
import { useState } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  Dashboardv20240509Api,
  dashboardv20240509Client
} from '@sequencehq/api/dashboard/v20240509'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'

type CustomerContact = NonNullable<
  Dashboardv20240509Api.PostCustomerContact.Response['data']
>

const useModalCreateCustomerContact = ({
  customerId,
  onSuccess
}: {
  customerId: string
  onSuccess: (newContact: CustomerContact) => void
}) => {
  const customerQuery = useQuery(dashboard20240730Client.getCustomer, {
    id: customerId
  })

  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false)
  const [apiErrors, setApiErrors] = useState<Record<
    string,
    ValidationError[]
  > | null>(null)

  const { fields, queries } = useForm<{
    name: string
    email: string
    purposes: CustomerContact['purposes']
    notificationSubscriptions: CustomerContact['notificationSubscriptions']
  }>({
    value: {
      name: '',
      email: '',
      purposes: [],
      notificationSubscriptions: ['BILLING']
    },
    reinitializeOnValueChange: false,
    showValidationErrors,
    apiErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'email',
        validation: [required, isEmail]
      },
      {
        property: 'purposes'
      },
      {
        property: 'notificationSubscriptions'
      }
    ]
  })

  const onSubmit = async () => {
    setShowValidationErrors(true)

    if (!queries.isValid) {
      return false
    }

    setApiErrors(null)
    const res = await dashboardv20240509Client.postCustomerContact({
      customerId,
      body: {
        name: fields.name.value,
        email: fields.email.value,
        purposes: fields.purposes.value,
        notificationSubscriptions: fields.notificationSubscriptions.value
      }
    })

    if (res.error || !res.data) {
      const parsedServerErrors = await mapApiResponseToFieldValidationErrors(
        res.res
      )
      setApiErrors(parsedServerErrors)
      setShowValidationErrors(true)

      return false
    }

    onSuccess(res.data)
    return true
  }

  return {
    isLoading: customerQuery.isLoading,
    modalTitle: `Add recipient for ${customerQuery.data?.legalName ?? ''}`,
    fields,
    onSubmit
  }
}

export const CreateCustomerContactModal = ({
  onClose,
  onSuccess,
  customerId
}: {
  onClose: () => void
  onSuccess: (newContact: CustomerContact) => void
  customerId: string
}) => {
  const { modalTitle, fields, onSubmit } = useModalCreateCustomerContact({
    customerId,
    onSuccess
  })

  return (
    <SimpleModal
      data-testid="customer.contacts.new"
      title={modalTitle}
      onSubmit={onSubmit}
      submitButtonText="Add recipient"
      cancelButtonText="Cancel"
      isOpen
      onClose={onClose}
    >
      <ContactModalForm fieldsConfig={fields} />
    </SimpleModal>
  )
}

export const CreateRecipientModal = (props: { onClose: () => void }) => {
  const ctx = useCubeContext()
  const { displayNotification } = useNotifications()

  const onSuccess = (newContact: CustomerContact) => {
    ctx.mutators.updateData({
      quote: {
        contacts: [
          ...ctx.queries.rawData.data.quote.contacts,
          {
            contactId: newContact.id,
            name: newContact.name,
            email: newContact.email,
            signedAt: undefined
          }
        ]
      }
    })
    displayNotification('Recipient added', { type: 'success' })
    props.onClose()
  }

  if (!ctx.queries.rawData.data.common.customerId) {
    return null
  }

  return (
    <CreateCustomerContactModal
      customerId={ctx.queries.rawData.data.common.customerId}
      onClose={props.onClose}
      onSuccess={onSuccess}
    />
  )
}
